import { metricDisplayLabel } from '../../lib/dashboard-capabilities/types/metric';

/**
 * The metric labels dash-meta stamps on a GENERATED WIDGET TITLE.
 *
 * Lives here, not in dash-meta, for one reason: the assistant's narration has
 * to agree with the title the user is looking at, and the two label sets are
 * maintained by hand over one metric catalog. While they sat in different apps
 * — which cannot import each other — nothing could compare them, so a new
 * entry on either side drifted silently and the same digest block could name
 * one chart two ways (stage report, 2026-08-11).
 *
 * `metric-labels.spec.ts` now fails the build on that drift. This is still a
 * BRIDGE, not the fix: two label maps over one catalog is the real defect, and
 * the fix is for the title generator to read `groupedMetricOptions` directly.
 */
export const GENERATED_TITLE_METRIC_LABELS: Record<string, string> = {
  headcount: 'Headcount',
  growth_rate: 'Growth Rate',
  hiring_rate: 'Hiring Rate',
  attrition_rate: 'Attrition Rate',
  tenure: 'Tenure',
  salary: 'Salary',
  total_compensation: 'Total Compensation',
  base_salary: 'Base Salary',
  contractor_pay_rate: 'Contractor Pay Rate',
  postings_active: 'Active Postings',
  postings_new: 'New Postings',
  postings_removed: 'Removed Postings',
  postings_volume: 'Posting Volume',
  posting_salary: 'Posting Salary',
  time_to_fill: 'Time to Fill',
  transitions_inflow: 'Talent Inflow',
  transitions_outflow: 'Talent Outflow',
  overall: 'Overall Sentiment',
  sentiment_top_bottom: 'Sentiment Drivers',
};

/** Humanize an unmapped key the way the title generator always has. */
const humanize = (metric: string): string =>
  metric
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/** The title dash-meta stamps on a generated widget. */
export function generatedTitleMetricLabel(metric: string): string {
  return GENERATED_TITLE_METRIC_LABELS[metric] ?? humanize(metric);
}

/**
 * Metrics whose CATALOG label disagrees with the widget TITLE, where the TITLE
 * is the better name for prose. The user reads the narration while LOOKING at
 * that title, so the narration follows it.
 *
 * Every entry must be a real divergence — `metric-labels.spec.ts` fails on an
 * alias that merely restates the catalog label, so this cannot accumulate
 * no-ops. Keys the title generator merely HUMANIZES are deliberately absent:
 * there the catalog label ("Work-Life Balance Sentiment", "Demand/Supply") is
 * the better name, and aliasing to the title would make the prose worse.
 */
export const NARRATION_METRIC_ALIASES: Record<string, string> = {
  // catalog "Hourly Rate" — the generated title, and the plainer phrase in prose.
  contractor_pay_rate: 'Contractor Pay Rate',
  // catalog "Postings Volume" — cosmetic, but a needless mismatch in one block.
  postings_volume: 'Posting Volume',
  // catalog "Inflow" / "Outflow" — bare directions read as fragments in prose.
  transitions_inflow: 'Talent Inflow',
  transitions_outflow: 'Talent Outflow',
};

/**
 * The name a metric is given in text the MODEL reads: the generated-title alias
 * when one exists, else the metric catalog's label (which humanizes an unknown
 * key rather than leaking it). Every model-facing render of a metric goes
 * through here.
 */
export function narrationMetricLabel(metric: string): string {
  return NARRATION_METRIC_ALIASES[metric] ?? metricDisplayLabel(metric);
}
